/* eslint-disable i18next/no-literal-string */
import { dayjs } from 'date';
import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, type DatePickerProps, FormControl, FormHelperText } from 'ui';

type Props = DatePickerProps & {
  required?: boolean;
  name: string;
  label: string;
  autocomplete?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  pickerTestId?: string;
  clearable?: boolean;
};

export const DateField = forwardRef<HTMLDivElement, Props>(function DateField(
  {
    label,
    name,
    autocomplete,
    disabled = false,
    required = false,
    helperText,
    size = 'small',
    // No default: MUI derives the format from the active adapterLocale (the user's locale).
    format,
    pickerTestId = 'date-button',
    clearable = true,
    onChange,
    ...rest
  },
  ref
) {
  const { control } = useFormContext();
  const { slotProps: pickerSlotProps, ...pickerProps } = rest;

  return (
    <Controller
      control={control}
      name={name}
      rules={
        required
          ? {
              required: 'This field is required',
            }
          : {}
      }
      render={({ field, formState }) => {
        const error = formState.errors[name];
        const { value, ...restField } = field;
        const resolvedValue = typeof value === 'string' ? dayjs(value) : (value ?? null);
        return (
          <FormControl required={required} error={Boolean(error)} fullWidth>
            <DatePicker
              label={label}
              disabled={disabled}
              {...restField}
              {...pickerProps}
              format={format}
              ref={ref}
              value={resolvedValue}
              onChange={(date, context) => {
                onChange?.(date, context);
                field.onChange(date ?? null);
              }}
              name={autocomplete || name}
              slotProps={{
                ...pickerSlotProps,
                openPickerButton: {
                  ...pickerSlotProps?.openPickerButton,
                  // @ts-expect-error - data-testid is a valid prop, despite not being in the type
                  'data-testid': pickerTestId,
                },
                textField: {
                  ...pickerSlotProps?.textField,
                  error: Boolean(error),
                  required,
                  size,
                },
                ...(clearable && {
                  clearButton: {
                    ...pickerSlotProps?.clearButton,
                    'data-testid': 'clear-date-button',
                  },
                  field: {
                    ...pickerSlotProps?.field,
                    clearable: true,
                    onClear: () => field.onChange(null),
                  },
                }),
              }}
            />
            {error || helperText ? (
              <FormHelperText>{error ? String(error?.message) : helperText}</FormHelperText>
            ) : null}
          </FormControl>
        );
      }}
    />
  );
});
